import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface YouTubePlayerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  url: string;
  title: string;
}

function extractVideoId(url: string): string | null {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
}

function PlayerContent({ url, title }: { url: string; title: string }) {
  const videoId = extractVideoId(url);
  if (!videoId) return <p className="text-sm text-muted-foreground">Could not load video.</p>;

  return (
    <div className="space-y-3">
      <AspectRatio ratio={16 / 9}>
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full rounded-md"
        />
      </AspectRatio>
      <Button variant="outline" size="sm" className="w-full" asChild>
        <a href={url} target="_blank" rel="noopener noreferrer">
          <ExternalLink className="h-3 w-3 mr-2" />
          Open in YouTube
        </a>
      </Button>
    </div>
  );
}

export default function YouTubePlayerDialog({ open, onOpenChange, url, title }: YouTubePlayerDialogProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="text-sm truncate">{title}</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-4">
            <PlayerContent url={url} title={title} />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="text-sm truncate">{title}</DialogTitle>
        </DialogHeader>
        <PlayerContent url={url} title={title} />
      </DialogContent>
    </Dialog>
  );
}
